/* =====================================================================
 * @auw/auth — domain types for the auth provider abstraction.
 *
 * The Samsung on-premise build is wired for Knox SSO. The remaining
 * provider shapes are stubs kept for future tenant-specific adapters.
 *
 * Phase 5 introduces the interface without committing to any of those
 * implementations:
 *   - `AuthProvider` is the contract every UI screen consumes.
 *   - SAML / OIDC / email+password factories accept the same shape
 *     but throw `NotImplementedError` at runtime; tenants that need
 *     them will swap in real adapters in later phases.
 *
 * ===================================================================== */

/** Stable discriminator for provider variants. Strings (not numbers)
 *  so we can serialise them into a tenant manifest without tripping
 *  over unions. */
export type AuthProviderKind =
  | 'saml'
  | 'oidc'
  | 'email_password'
  | 'knox' | 'google_oauth';

/** Successful login — web flow (refresh token handled by an HttpOnly
 *  cookie issued by the backend). */
export interface AuthSessionResult {
  accessToken: string;
  /** Seconds-to-expiry for the access token. */
  expiresIn: number;
  isNewUser: boolean;
}

/** Successful login — desktop flow. The refresh token is returned in
 *  the body so the Tauri shell can persist it in the OS keychain. */
export interface DesktopAuthSessionResult extends AuthSessionResult {
  refreshToken: string;
}

/** Options for `AuthProvider.startLogin`. `redirectUri` is the
 *  absolute URL the OAuth/SAML/OIDC dance should bounce back to
 *  after the user authenticates. */
export interface StartLoginOptions {
  redirectUri?: string;
  tenantIdentityOverride?: Record<string, string | number | boolean | null>;
}

/** Options for `AuthProvider.handleCallback`. `desktop` selects the
 *  body-based refresh-token flow; unset / false uses cookie flow. */
export interface HandleCallbackOptions {
  code: string;
  desktop?: boolean;
}

/**
 * Runtime auth provider. A single instance corresponds to one login
 * button in the UI; `useAuthProviders()` returns the array the app
 * should iterate over when rendering the auth screen.
 *
 * Providers must be pure values — no React, no module-level state —
 * so tenants can load them lazily and replace them at runtime.
 */
export interface AuthProvider {
  /** Stable id, unique across the tenant's provider list. Used as
   *  the React key and as the persisted preference id for
   *  "remember last used provider". */
  readonly id: string;
  readonly kind: AuthProviderKind;
  /** i18n key for the CTA label; the consuming app resolves it. */
  readonly labelKey?: string;
  /** Asset path used for the button icon (logo). */
  readonly iconSrc?: string;
  /**
   * Start the login flow. For OAuth this typically redirects the
   * browser; for email+password this opens a modal. The return
   * value is `void | Promise<void>` rather than a session because
   * OAuth providers never synchronously produce one — the session
   * only becomes available after the callback.
   */
  startLogin(options?: StartLoginOptions): void | Promise<void>;
  /**
   * Build the auth-start URL without navigating. Only OAuth / OIDC
   * / SAML providers implement this — it exists specifically so the
   * Tauri desktop shell can hand the URL to `openExternalUrl()`
   * instead of navigating the in-app WebView. Absent on
   * email+password providers which sign in synchronously.
   */
  getLoginUrl?(options?: StartLoginOptions): string;
  /**
   * Handle the provider's callback (typically an OAuth `code`).
   * Only OAuth-style providers implement this; email+password
   * providers sign-in inline and won't.
   */
  handleCallback?(
    options: HandleCallbackOptions,
  ): Promise<AuthSessionResult | DesktopAuthSessionResult>;
}

/* ------------------------------------------------------------------ */
/* Provider config shapes (factory inputs).                            */
/* ------------------------------------------------------------------ */

/** Shared base for every config shape. */
interface AuthProviderConfigBase {
  /** Stable id — e.g. `'google'`, `'okta-saml'`, `'keycloak-oidc'`. */
  readonly id: string;
  /** i18n key override for the button label. Factories supply a
   *  kind-appropriate default when this is omitted. */
  readonly labelKey?: string;
  /** Optional icon asset path. Tenant-relative paths are resolved by
   *  `@auw/tenant`'s `absoluteAssetUrl()` before rendering. */
  readonly iconSrc?: string;
}

/** SAML — stub. Actual implementation lands with Phase 10+. */
export interface SamlProviderConfig extends AuthProviderConfigBase {
  readonly kind: 'saml';
  /** Entity id / IdP metadata endpoint. Retained here for future use
   *  but not consumed by the stub adapter. */
  readonly idpEntityId?: string;
  readonly idpMetadataUrl?: string;
}

/** OIDC — stub. Actual implementation lands with Phase 10+. */
export interface OidcProviderConfig extends AuthProviderConfigBase {
  readonly kind: 'oidc';
  readonly issuerUrl?: string;
  readonly clientId?: string;
}

/** Email + password — stub. Actual implementation lands with
 *  Phase 10+. Designed so B2B tenants with air-gapped directories can
 *  wire up their own in-house login form. */
export interface EmailPasswordProviderConfig extends AuthProviderConfigBase {
  readonly kind: 'email_password';
}

/** Knox SSO — tenant-specific local-tray SSO provider. */
export interface KnoxProviderConfig extends AuthProviderConfigBase {
  readonly kind: 'knox';
}

/** Discriminated union of every provider config shape. */
export type AuthProviderConfig =
  | SamlProviderConfig
  | OidcProviderConfig
  | EmailPasswordProviderConfig
  | KnoxProviderConfig | GoogleOAuthProviderConfig;

/**
 * Thrown by stub factories when their `startLogin` / `handleCallback`
 * is invoked. Carrying the `kind` on the error lets the app surface a
 * more helpful message ("SAML provider is not configured for this
 * tenant") rather than a generic failure.
 */
export class NotImplementedError extends Error {
  constructor(public readonly kind: AuthProviderKind, providerId: string) {
    super(
      `[auth] Provider ${JSON.stringify(
        providerId,
      )} of kind ${JSON.stringify(kind)} is not implemented. ` +
        'Supply a real adapter in the tenant config.',
    );
    this.name = 'NotImplementedError';
  }
}

// Restored B2C API and type contracts.
export interface GoogleOAuthProviderConfig extends AuthProviderConfigBase {
  readonly kind: 'google_oauth';
  /** Build the URL the browser should navigate to in order to start
   *  the Google OAuth dance. B2C passes
   *  `api/client.ts#buildGoogleLoginUrl` here. */
  buildLoginUrl(redirectUri?: string): string;
  /** Exchange the `code` returned on the callback for a web session.
   *  B2C passes `api/client.ts#exchangeAuthCode` here. */
  exchangeAuthCode(code: string): Promise<AuthSessionResult>;
  /** Desktop variant that returns the refresh token in the body.
   *  B2C passes `api/client.ts#exchangeAuthCodeForDesktop` here. */
  exchangeAuthCodeForDesktop?(code: string): Promise<DesktopAuthSessionResult>;
}
